import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Switch, 
  Button, 
  message,
  Row,
  Col,
  Upload,
  Image,
  Space,
  Divider,
  Tabs,
  Tree,
  Tag
} from 'antd';
import { 
  SaveOutlined, 
  ArrowLeftOutlined,
  UploadOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { productGroupService } from '../services/productGroupService';
import { commonAttachFileService } from '../services/commonAttachFileService';
import type { ProductGroup } from '../types/productGroup';
import { PRODUCT_GROUP_CATEGORY_MAP } from '../constants/productGroupCategory';

const { Option } = Select;
const { TextArea } = Input;

const ProductGroupEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [productGroup, setProductGroup] = useState<ProductGroup | null>(null);
  // 多语言logo状态
  const [i18nLogoUrls, setI18nLogoUrls] = useState<Record<string, string>>({});
  const [i18nLogoUploading, setI18nLogoUploading] = useState<Record<string, boolean>>({});
  // 规格相关状态
  const [specTreeData, setSpecTreeData] = useState<any[]>([]);
  const [selectedSpecIds, setSelectedSpecIds] = useState<number[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
  const [availableSpecIds, setAvailableSpecIds] = useState<Set<number>>(new Set());

  const isEdit = !!id;

  useEffect(() => {
    if (isEdit && id) {
      const loadData = async () => {
        await fetchProductGroupDetail(id);
        await fetchAvailableSpecs(id);
      };
      loadData();
    }
  }, [id, isEdit]);


  // 获取商品组的可选规格列表（包含规格及其父级）
  const fetchAvailableSpecs = async (groupId: string) => {
    try {
      console.log('开始获取可选规格，商品组ID:', groupId);
      
      // 调用接口获取可选规格列表（包含规格及其父级）
      const response = await productGroupService.getAvailableSpecs(groupId);
      const specs = response?.data || response || [];
      
      console.log('获取到的可选规格列表:', specs);
      
      if (!specs || specs.length === 0) {
        console.log('没有找到可选规格');
        setSpecTreeData([]);
        setAvailableSpecIds(new Set());
        return;
      }
      
      // 提取所有可选的规格ID（content类型）
      const allSpecIds = new Set<number>();
      const extractSpecIds = (items: any[]) => {
        items.forEach(item => {
          if (item.category === 'content') {
            allSpecIds.add(item.id);
          }
          if (item.children && item.children.length > 0) {
            extractSpecIds(item.children);
          }
        });
      };
      extractSpecIds(specs);
      
      setAvailableSpecIds(allSpecIds);
      
      // 构建树形数据
      const buildTreeData = (items: any[]): any[] => {
        return items.map(item => {
          const treeNode: any = {
            title: item.name,
            key: item.id.toString(),
            id: item.id,
            category: item.category,
            name: item.name,
            pId: item.pId,
            disabled: item.category !== 'content' // 只有content类型可以选择
          };
          
          if (item.children && item.children.length > 0) {
            treeNode.children = buildTreeData(item.children);
          }
          
          return treeNode;
        });
      };
      
      const treeData = buildTreeData(specs);
      console.log('构建的规格树数据:', treeData);
      setSpecTreeData(treeData);
    } catch (error) {
      console.error('获取可选规格失败:', error);
      message.error('获取可选规格失败');
    }
  };

  // 获取商品组详情
  const fetchProductGroupDetail = async (groupId: string) => {
    try {
      setLoading(true);
      const response = await productGroupService.getProductGroupDetail(groupId);
      const data = response.data;
      
      if (data) {
        setProductGroup(data);
        
        // 设置表单基础数据
        const formValues: any = {
          name: data.name,
          category: data.category,
          remark: data.remark,
          isShow: data.isShow === 1,
          seq: data.seq,
          specialTips: data.specialTips,
          sdkProductId: data.sdkProductId
        };
        
        // 设置多语言数据
        if (data.i18n) {
          formValues.i18n = data.i18n;
          // 设置多语言logo状态
          Object.keys(data.i18n).forEach(lang => {
            if (data.i18n![lang]?.logoUrl) {
              setI18nLogoUrls(prev => ({ ...prev, [lang]: data.i18n![lang].logoUrl! }));
            }
          });
        } else {
          // 如果没有 i18n 数据，初始化空对象
          formValues.i18n = {
            zh: {
              name: data.name || '',
              remark: data.remark || '',
              specialTips: data.specialTips || '',
              logoUrl: data.logoUrl || ''
            },
            en: { name: '', remark: '', specialTips: '', logoUrl: '' },
            pl: { name: '', remark: '', specialTips: '', logoUrl: '' }
          };
        }
        
        // 设置规格ID列表
        if (data.specIds && data.specIds.length > 0) {
          setSelectedSpecIds(data.specIds);
          setCheckedKeys(data.specIds.map((id: number) => id.toString()));
        }
        
        form.setFieldsValue(formValues);
      } else {
        message.error('商品组不存在');
        navigate('/product-group');
      }
      
    } catch (error) {
      console.error('获取商品组详情失败:', error);
      message.error('获取商品组详情失败');
      navigate('/product-group');
    } finally {
      setLoading(false);
    }
  };

  // 多语言LOGO上传处理
  const handleI18nLogoUpload = async (file: File, lang: string) => {
    setI18nLogoUploading(prev => ({ ...prev, [lang]: true }));
    try {
      const response = await commonAttachFileService.uploadUrl(file);
      const uploadedUrl = response.data;
      setI18nLogoUrls(prev => ({ ...prev, [lang]: uploadedUrl }));
      form.setFieldsValue({ [`i18n.${lang}.logoUrl`]: uploadedUrl });
      message.success('LOGO上传成功');
    } catch (error) {
      console.error('LOGO上传失败:', error);
      message.error('LOGO上传失败');
    } finally {
      setI18nLogoUploading(prev => ({ ...prev, [lang]: false }));
    }
    return false;
  };

  // 删除多语言LOGO
  const handleRemoveI18nLogo = (lang: string) => {
    setI18nLogoUrls(prev => {
      const newUrls = { ...prev };
      delete newUrls[lang];
      return newUrls;
    });
    form.setFieldsValue({ [`i18n.${lang}.logoUrl`]: '' });
    message.success('LOGO已删除');
  };

  // 处理树形选择器的选择
  const handleTreeCheck = (checked: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }, info: any) => {
    // 处理checked参数，可能是数组或对象
    const checkedKeys = Array.isArray(checked) ? checked : checked.checked;
    
    // 只处理选中的节点，过滤掉半选中的父节点
    const checkedNodes = info.checkedNodes || [];
    const selectedIds = checkedNodes
      .filter((node: any) => node.category === 'content')
      .map((node: any) => node.id);
    
    setSelectedSpecIds(selectedIds);
    setCheckedKeys(checkedKeys);
  };

  // 保存商品组
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // 提取多语言数据
      const i18nData = values.i18n || {};
      
      // 处理多语言logo
      const processedI18nData: any = {};
      for (const lang of ['zh', 'en', 'pl']) {
        if (i18nData[lang]) {
          processedI18nData[lang] = { ...i18nData[lang] };
          if (i18nLogoUrls[lang]) {
            processedI18nData[lang].logoUrl = i18nLogoUrls[lang];
          }
        }
      }
      
      // 中文数据作为主表数据
      const mainName = processedI18nData.zh?.name || values.name || '';
      const mainRemark = processedI18nData.zh?.remark || values.remark || '';
      const mainSpecialTips = processedI18nData.zh?.specialTips || values.specialTips || '';
      const mainLogoUrl = processedI18nData.zh?.logoUrl || '';

      const submitData = {
        ...values,
        name: mainName, // 主表使用中文数据
        remark: mainRemark,
        specialTips: mainSpecialTips,
        logoUrl: mainLogoUrl, // 主表使用中文logo
        isShow: values.isShow ? 1 : 0,
        id: isEdit ? id : undefined,
        specIds: selectedSpecIds, // 添加规格ID列表
        i18n: processedI18nData // 传递处理后的多语言数据到后端
      };

      let response;
      if (isEdit) {
        response = await productGroupService.updateProductGroup(submitData);
      } else {
        response = await productGroupService.addProductGroup(submitData);
      }

      if (response.status === 200) {
        message.success(isEdit ? '更新商品组成功' : '新增商品组成功');
        navigate('/product-group');
      } else {
        message.error('操作失败');
      }
    } catch (error) {
      console.error('保存商品组失败:', error);
      message.error('保存商品组失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/product-group')}
          >
            返回
          </Button>
          <h1 className="page-title">
            {isEdit ? '编辑商品组' : '新增商品组'}
          </h1>
        </Space>
      </div>

      <Card className="content-card" loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="商品组名称（中文）"
                name="name"
                rules={[{ required: true, message: '请输入商品组名称（中文）' }]}
              >
                <Input placeholder="请输入商品组名称（中文）" />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                label="商品组类型"
                name="category"
                rules={[{ required: true, message: '请选择商品组类型' }]}
              >
                <Select placeholder="请选择商品组类型">
                  {Object.entries(PRODUCT_GROUP_CATEGORY_MAP).map(([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="多语言配置">
            <Tabs
              items={[
                {
                  key: 'zh',
                  label: '🇨🇳 中文',
                  children: (
                    <div>
                      <Form.Item
                        label="商品组名称"
                        name={['i18n', 'zh', 'name']}
                        rules={[{ required: true, message: '请输入商品组名称（中文）' }]}
                      >
                        <Input placeholder="请输入商品组名称（中文）" />
                      </Form.Item>
                      <Form.Item
                        label="备注"
                        name={['i18n', 'zh', 'remark']}
                      >
                        <TextArea rows={4} placeholder="请输入备注（中文）" />
                      </Form.Item>
                      <Form.Item
                        label="特殊提示"
                        name={['i18n', 'zh', 'specialTips']}
                      >
                        <TextArea rows={3} placeholder="请输入特殊提示（中文）" maxLength={200} showCount />
                      </Form.Item>
                      <Form.Item
                        label="LOGO（中文）"
                        name={['i18n', 'zh', 'logoUrl']}
                      >
                        <div>
                          {i18nLogoUrls['zh'] && (
                            <div style={{ marginBottom: 16, position: 'relative', display: 'inline-block' }}>
                              <Image
                                width={100}
                                height={100}
                                src={i18nLogoUrls['zh']}
                                alt="LOGO"
                                style={{ borderRadius: '6px', objectFit: 'cover' }}
                              />
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                style={{ position: 'absolute', top: -8, right: -8 }}
                                onClick={() => handleRemoveI18nLogo('zh')}
                              />
                            </div>
                          )}
                          <Upload
                            beforeUpload={(file) => handleI18nLogoUpload(file, 'zh')}
                            showUploadList={false}
                            disabled={i18nLogoUploading['zh']}
                          >
                            <Button icon={<UploadOutlined />} loading={i18nLogoUploading['zh']}>
                              {i18nLogoUrls['zh'] ? '重新上传' : '上传LOGO'}
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                    </div>
                  )
                },
                {
                  key: 'en',
                  label: '🇺🇸 English',
                  children: (
                    <div>
                      <Form.Item
                        label="商品组名称"
                        name={['i18n', 'en', 'name']}
                      >
                        <Input placeholder="请输入商品组名称（English）" />
                      </Form.Item>
                      <Form.Item
                        label="备注"
                        name={['i18n', 'en', 'remark']}
                      >
                        <TextArea rows={4} placeholder="请输入备注（English）" />
                      </Form.Item>
                      <Form.Item
                        label="特殊提示"
                        name={['i18n', 'en', 'specialTips']}
                      >
                        <TextArea rows={3} placeholder="请输入特殊提示（English）" maxLength={200} showCount />
                      </Form.Item>
                      <Form.Item
                        label="LOGO（English）"
                        name={['i18n', 'en', 'logoUrl']}
                      >
                        <div>
                          {i18nLogoUrls['en'] && (
                            <div style={{ marginBottom: 16, position: 'relative', display: 'inline-block' }}>
                              <Image
                                width={100}
                                height={100}
                                src={i18nLogoUrls['en']}
                                alt="LOGO"
                                style={{ borderRadius: '6px', objectFit: 'cover' }}
                              />
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                style={{ position: 'absolute', top: -8, right: -8 }}
                                onClick={() => handleRemoveI18nLogo('en')}
                              />
                            </div>
                          )}
                          <Upload
                            beforeUpload={(file) => handleI18nLogoUpload(file, 'en')}
                            showUploadList={false}
                            disabled={i18nLogoUploading['en']}
                          >
                            <Button icon={<UploadOutlined />} loading={i18nLogoUploading['en']}>
                              {i18nLogoUrls['en'] ? '重新上传' : '上传LOGO'}
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                    </div>
                  )
                },
                {
                  key: 'pl',
                  label: '🇵🇱 Polski',
                  children: (
                    <div>
                      <Form.Item
                        label="商品组名称"
                        name={['i18n', 'pl', 'name']}
                      >
                        <Input placeholder="请输入商品组名称（Polski）" />
                      </Form.Item>
                      <Form.Item
                        label="备注"
                        name={['i18n', 'pl', 'remark']}
                      >
                        <TextArea rows={4} placeholder="请输入备注（Polski）" />
                      </Form.Item>
                      <Form.Item
                        label="特殊提示"
                        name={['i18n', 'pl', 'specialTips']}
                      >
                        <TextArea rows={3} placeholder="请输入特殊提示（Polski）" maxLength={200} showCount />
                      </Form.Item>
                      <Form.Item
                        label="LOGO（Polski）"
                        name={['i18n', 'pl', 'logoUrl']}
                      >
                        <div>
                          {i18nLogoUrls['pl'] && (
                            <div style={{ marginBottom: 16, position: 'relative', display: 'inline-block' }}>
                              <Image
                                width={100}
                                height={100}
                                src={i18nLogoUrls['pl']}
                                alt="LOGO"
                                style={{ borderRadius: '6px', objectFit: 'cover' }}
                              />
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                style={{ position: 'absolute', top: -8, right: -8 }}
                                onClick={() => handleRemoveI18nLogo('pl')}
                              />
                            </div>
                          )}
                          <Upload
                            beforeUpload={(file) => handleI18nLogoUpload(file, 'pl')}
                            showUploadList={false}
                            disabled={i18nLogoUploading['pl']}
                          >
                            <Button icon={<UploadOutlined />} loading={i18nLogoUploading['pl']}>
                              {i18nLogoUrls['pl'] ? '重新上传' : '上传LOGO'}
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>
                    </div>
                  )
                }
              ]}
            />
          </Form.Item>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="排序"
                name="seq"
                rules={[{ required: true, message: '请输入排序值' }]}
              >
                <InputNumber 
                  min={0} 
                  max={999} 
                  placeholder="排序值，数字越小越靠前"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                label="是否显示"
                name="isShow"
                valuePropName="checked"
              >
                <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="商品组规格"
            help={isEdit ? "从已绑定商品的规格中选择作为商品组的规格" : "请先保存商品组并添加商品，然后才能配置规格"}
          >
            <div style={{ 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px', 
              padding: '8px',
              maxHeight: '300px',
              overflow: 'auto',
              backgroundColor: '#fafafa'
            }}>
              {specTreeData.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                  {isEdit ? '加载中...' : '请先保存商品组并添加商品'}
                </div>
              ) : (
                <Tree
                  checkable
                  checkedKeys={checkedKeys}
                  onCheck={handleTreeCheck}
                  treeData={specTreeData}
                  showLine
                  showIcon={false}
                  style={{
                    background: 'transparent'
                  }}
                  titleRender={(nodeData: any) => (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: nodeData.disabled ? '#999' : '#333',
                      fontSize: '14px'
                    }}>
                      <span style={{ 
                        marginRight: '8px',
                        color: nodeData.category === 'option' ? '#1890ff' : '#52c41a'
                      }}>
                        {nodeData.category === 'option' ? '📁' : '📄'}
                      </span>
                      <span>{nodeData.title}</span>
                    </div>
                  )}
                />
              )}
            </div>
            <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
              已选择 {selectedSpecIds.length} 个规格（只能选择已绑定商品中包含的规格项）
            </div>
          </Form.Item>

          <Divider />

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SaveOutlined />}
                loading={loading}
              >
                {isEdit ? '更新' : '保存'}
              </Button>
              <Button onClick={() => navigate('/product-group')}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ProductGroupEdit;
